import React, { useEffect, useState } from "react"
import ClientReportCard from "../../components/ClientReportCard.molecule"
import { CLIENT_SPRINT_STATES } from "../../../../consts/modules/clientReport.consts"
import StatusChip from "../../../../components/molecules/StatusChip.molecule"
import { isObjectEmpty } from "../../../../utils/object.utils"
import { displayValueOrPlaceholder } from "../../../../utils/strings.utils"
import {
  ValueWithLabelContainer,
  ValueWithLabelTemplate,
} from "../../../../components/layouts/containers/ValueWithLabelElements.container"
import TeamChip, {
  TEAM_CHIP_TYPE,
} from "../../../../components/molecules/TeamChip.molecule"
import { getClientPeriodConsumptionSummary } from "../../../../services/clientReport.service"
import { isBudgetTypeCredit } from "../../../../utils/budget.utils"
import { numberFormatter } from "../../../../utils/numbers.utils"
import SprintApprovalTemplate from "../../components/SprintApprovalTemplate.molecule"
import {
  getBilledTimeTooltipByPeriod,
  getSelectedPeriodTitle,
  isPeriodInvalid,
} from "../../../../utils/clientReport.utils"

const ClientPeriodConsumptionSummary = ({ refresh, selectedPeriod = {} }) => {
  const [data, setData] = useState({})
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchData()
  }, [selectedPeriod, refresh])

  const fetchData = async () => {
    setLoading(true)
    try {
      if (isPeriodInvalid(selectedPeriod)) {
        setData({})
        return
      }
      const response = await getClientPeriodConsumptionSummary(
        selectedPeriod?.id,
        selectedPeriod?.clientConsumptionPeriodType
      )
      if (!response.success) {
        throw new Error(response.message)
      }
      setData(response.result || {})
    } catch (error) {
      console.error("Error fetching period consumption summary:", error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <ClientReportCard
      title={
        <div className="flex gap-2">
          <span>
            {getSelectedPeriodTitle({
              startDate: selectedPeriod?.startDate,
              clientConsumptionPeriodType:
                selectedPeriod?.clientConsumptionPeriodType,
              period: selectedPeriod,
              closeType: selectedPeriod?.closeType,
            })}
          </span>
          <StatusChip
            status={selectedPeriod?.state}
            tooltip={selectedPeriod?.state === CLIENT_SPRINT_STATES.APPROVED}
            tooltipContent={
              <SprintApprovalTemplate
                date={data?.approvalDate}
                approverFullName={`${
                  data?.approvedByName
                } ${data?.approvedByLastName}`}
              />
            }
            loading={loading}
          />
        </div>
      }
      iconClassName="pi pi-calendar text-3xl"
      className="flex-1 font-cairo flex-shrink-0 w-full"
    >
      <ValueWithLabelContainer>
        <ValueWithLabelTemplate
          label="Horas registradas"
          value={data?.totalBilledTime ?? "-"}
          labelTooltip
          labelTooltipContent={getBilledTimeTooltipByPeriod(
            selectedPeriod?.clientConsumptionPeriodType
          )}
          labelTooltipPosition="top"
          labelTooltipIcon="pi pi-info-circle"
          loading={loading}
        />
        <ValueWithLabelTemplate
          label="Participantes"
          value={displayValueOrPlaceholder(data?.totalTeamMembers)}
          labelTooltip
          labelTooltipContent={getActiveTeamMembersTemplate({
            teamMembersByArea: data?.clientPeriodConsumedHoursGroupedByArea,
          })}
          labelTooltipPosition="top"
          labelTooltipIcon="pi pi-info-circle"
          loading={loading}
        />
        {!isBudgetTypeCredit(data?.budgetTypeId) && (
          <ValueWithLabelTemplate
            label="Horas totales facturadas"
            value={displayValueOrPlaceholder(data?.consumedHoursSinceLastBag)}
            labelTooltip
            labelTooltipContent={
              "Horas totales facturadas del presupuesto activo"
            }
            labelTooltipPosition="top"
            labelTooltipIcon="pi pi-info-circle"
            loading={loading}
          />
        )}
        <ValueWithLabelTemplate
          label="Rendimiento"
          value={
            (data?.totalBilledTime * 100) / data?.totalSisuTime ?
              `${numberFormatter(
                (data?.totalBilledTime * 100) / data?.totalSisuTime
              )}%`
            : "-"
          }
          labelTooltip
          labelTooltipContent={performanceTooltipTemplate({
            totalSisuTime: data?.totalSisuTime,
            totalBilledTime: data?.totalBilledTime,
          })}
          labelTooltipPosition="top"
          labelTooltipIcon="pi pi-info-circle"
          loading={loading}
        />
      </ValueWithLabelContainer>
    </ClientReportCard>
  )
}

const getActiveTeamMembersTemplate = ({ teamMembersByArea = {} }) => {
  if (!teamMembersByArea || isObjectEmpty(teamMembersByArea)) {
    return "No hay integrantes activos"
  }
  return (
    <table className="w-10rem h-fit">
      <tbody>
        {Object.entries(teamMembersByArea).map(
          ([team, { label, totalTeamMembers }], index, array) => (
            <tr key={team}>
              <td className={`${index === array.length - 1 ? "pb-0" : "pb-2"}`}>
                <TeamChip
                  teamTag={team}
                  teamLabel={label}
                  type={TEAM_CHIP_TYPE.SQUARE}
                />
              </td>
              <td className="text-right">{totalTeamMembers}</td>
            </tr>
          )
        )}
      </tbody>
    </table>
  )
}

const performanceTooltipTemplate = ({
  totalSisuTime = 0,
  totalBilledTime = 0,
}) => {
  return (
    <div className="flex flex-column gap-3">
      <span>
        Porcentaje de horas facturadas respecto al total de horas trabajadas
      </span>
      <div className="flex flex-column gap-2">
        <div className="flex justify-content-between">
          <span>Horas trabajadas</span>
          <span>{displayValueOrPlaceholder(totalSisuTime)}</span>
        </div>
        <div className="flex justify-content-between">
          <span>Horas perdonadas</span>
          <span>
            {displayValueOrPlaceholder(totalSisuTime - totalBilledTime)}
          </span>
        </div>
      </div>
      <span>*Este dato no se incluye en reporte descargable</span>
    </div>
  )
}

export default ClientPeriodConsumptionSummary
